import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Button, Flex, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { useFeatureFlag } from "@jsq/flags-react";
import { useAppContext } from "~/contexts/appContext";
import { SIDEBAR_TREASURY_BASE_ITEMS } from "~/pages/Treasury/Sidebar/consts";
import {
  hasTreasuryAccessOnAnyArena,
  hasTreasuryConfigAccess,
  isTreasuryAdmin,
  isTreasuryUser,
} from "~/utils/userRoles";
import * as styles from "../styles_vanilla.css";
import { ParentNavigationItem, BaseNavigationItem } from "./types";
import { hasSubItems, isActiveRoute } from "./utils";

const TreasurySidebar: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const [expandedParent, setExpandedParent] = useState<string | null>(null);
  const { user } = useAppContext();
  const clientLevelPermissionsEnabled = useFeatureFlag("treasuryClientLevelPermissions", false);
  const navigationItems: ParentNavigationItem[] = useMemo(() => {
    const hasTreasuryAccess = clientLevelPermissionsEnabled
      ? hasTreasuryAccessOnAnyArena(user.roles)
      : isTreasuryUser(user.roles);
    if (!hasTreasuryAccess) {
      return [];
    }
    return SIDEBAR_TREASURY_BASE_ITEMS;
  }, [user.roles, clientLevelPermissionsEnabled]);

  const handleNavigation = (path: string) => {
    const targetParent =
      getParentOfSubItem(path) || navigationItems.find((item) => item.paths.includes(path));

    // If navigating to a sub-item, expand its parent
    if (getParentOfSubItem(path)) {
      setExpandedParent(targetParent!.title);
    } else {
      // If navigating to a parent item directly, close any open dropdown
      setExpandedParent(null);
    }
    history.push(path);
  };

  const toggleExpandedItem = (itemTitle: string) => {
    setExpandedParent(expandedParent === itemTitle ? null : itemTitle);
  };

  const hasActiveSubItem = (item: ParentNavigationItem): boolean => {
    if (!hasSubItems(item)) return false;
    return item.subItems!.some((subItem) => isActiveRoute(subItem.path, location.pathname));
  };

  const getParentOfSubItem = (subItemPath: string): ParentNavigationItem | null => {
    return (
      navigationItems.find(
        (item) =>
          hasSubItems(item) && item.subItems!.some((subItem) => subItem.path === subItemPath),
      ) || null
    );
  };

  const hasConfigurationAccess = useMemo(
    () =>
      clientLevelPermissionsEnabled
        ? isTreasuryAdmin(user.roles) || hasTreasuryConfigAccess(user.roles)
        : hasTreasuryConfigAccess(user.roles),
    [user.roles, clientLevelPermissionsEnabled],
  );

  const isConfigurationActive = useMemo(
    () => location.pathname.startsWith("/treasury/configuration"),
    [location.pathname],
  );

  const handleConfigurationClick = useCallback(() => {
    history.push("/treasury/configuration");
  }, [history]);

  return (
    <div className={styles.sidebarDiv}>
      <Flex flexDirection="column" gap={8} className={styles.navigationContainer}>
        {navigationItems.map((item) => (
          <div key={item.title}>
            {hasSubItems(item) ? (
              <>
                {/* Could not use Button from Design System since it doesn't allow for multiple icons and strings in it */}
                <button
                  onClick={() => toggleExpandedItem(item.title)}
                  className={`${styles.parentNavigationItem} ${
                    item.paths.some((path) => isActiveRoute(path, location.pathname)) ||
                    (expandedParent !== item.title && hasActiveSubItem(item))
                      ? styles.activeButton
                      : styles.inactiveButton
                  }`}
                >
                  <Icon type={item.iconType} size="medium" className={styles.navigationIcon} />
                  <Text className={styles.dropdownText} variant="strong-md">
                    {item.title}
                  </Text>
                  <Icon
                    className={styles.dropdownButtonChevron}
                    type={expandedParent === item.title ? "chevron-up" : "chevron-down"}
                    size="medium"
                  />
                </button>
                {expandedParent === item.title && (
                  <>
                    {item.subItems!.map((subItem: BaseNavigationItem) => (
                      <Button
                        key={subItem.path}
                        variant={
                          isActiveRoute(subItem.path, location.pathname) ? "primary" : "neutral"
                        }
                        onClick={() => handleNavigation(subItem.path)}
                        className={`${styles.navigationButton} ${styles.subItemButton} ${
                          isActiveRoute(subItem.path, location.pathname)
                            ? styles.activeButton
                            : styles.inactiveButton
                        }`}
                        isFullWidth
                      >
                        {subItem.title}
                      </Button>
                    ))}
                  </>
                )}
              </>
            ) : (
              // Regular item without subItems - use Button component
              <button
                onClick={() => handleNavigation(item.paths[0])}
                className={`${styles.parentNavigationItem} ${
                  item.paths.some((path) => isActiveRoute(path, location.pathname))
                    ? styles.activeButton
                    : styles.inactiveButton
                }`}
              >
                <Icon type={item.iconType} size="medium" className={styles.navigationIcon} />
                <Text className={styles.dropdownText} variant="strong-md">
                  {item.title}
                </Text>
              </button>
            )}
          </div>
        ))}
      </Flex>
      {hasConfigurationAccess && (
        <div className={styles.configurationButtonContainer}>
          <button
            data-testid="configuration-button"
            onClick={handleConfigurationClick}
            className={`${styles.configurationButton} ${
              isConfigurationActive ? styles.activeButton : styles.inactiveButton
            }`}
          >
            <Icon type="cog" size="medium" className={styles.navigationIcon} />
            <Text variant="strong-md">Configuration</Text>
          </button>
        </div>
      )}
    </div>
  );
};

export default TreasurySidebar;
